import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { styled } from '../../utils/styled';
import { propStyles } from '../../utils/propStyles';
import DownloadControls from './DownloadControls';
import { DataStore, EntityStore, SuperscriptScope } from '../../stores';
import { DisplayOption } from '../../types';
import { action, computed } from 'mobx';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../../models/configuration/explorer/DownloadControls';

const DropDownDocumentListItem = styled<any, any>(
    'li',
    'dropDownDocumentListItem',
    'documentMenuListItem'
)`
    ${propStyles};
`;

// Dropdown
DropDownDocumentListItem.defaultProps = {
    alignItems: 'center',
    paddingX: 'md',
    paddingY: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    hover: {
        bgColor: 'gray100',
    },
};

// Inline
const InlineDocumentListItem = styled<any, any>(
    'li',
    'inlineDocumentListItem',
    'documentMenuListItem'
)`
    ${propStyles};
`;
InlineDocumentListItem.defaultProps = {
    display: 'flex',
    flex: '1 0 auto',
    alignItems: 'center',
    paddingX: 'xs',
    paddingY: 'sm',
    margin: '0 2px',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'none none solid none',
    hover: {
        bgColor: 'gray100',
    },
};

interface DocumentMenuListItemTheme {
    DropDownDocumentList?: any;
    InlineDocumentList?: any;
    DocumentMenuListItem?: any;
    DownloadControls?: any;
}

interface DocumentMenuListItemProps {
    theme: any;
    document: any;
    dataStore?: DataStore;
    showLabel?: boolean;
    showCheckbox?: boolean;
    displayOption?: DisplayOption;
    selected?: boolean;
    metricContext?: string;
    documentType?: string;
    entityStore?: EntityStore;
    superscriptScope?: SuperscriptScope;
    disableExtendedInfo?: boolean;
    /**
     * @title On Change
     *
     * @param document the document entity
     * @param key the key used to group the selected document by the entity
     */
    onChange?(document: any, key: string): void;
}

@inject('dataStore', 'entityStore')
@observer
class DocumentMenuListItem extends Component<DocumentMenuListItemProps> {
    constructor(props: DocumentMenuListItemProps) {
        super(props);
        this.handleOnChange = this.handleOnChange.bind(this);
    }

    @action
    handleOnChange(document) {
        const { onChange, entityStore } = this.props;
        if (onChange) {
            onChange(document, entityStore?.value?.clientCode);
        }
    }

    @computed
    get theme() {
        const { theme } = this.props;
        const componentTheme: DocumentMenuListItemTheme = {};
        return { ...theme, ...componentTheme };
    }

    @computed
    get downloadControlActions(): DownloadControlActions {
        const { dataStore } = this.props;
        return dataStore?.config.getDownloadControlActionsOrDefault();
    }

    @action
    downloadControls() {
        const {
            theme,
            document,
            dataStore,
            showLabel = true,
            showCheckbox = false,
            selected = false,
            metricContext,
            documentType,
            entityStore,
            superscriptScope,
            disableExtendedInfo,
        } = this.props;

        if (!dataStore) {
            return null;
        }

        const {
            getDocumentDisplayName,
            downloadDocuments,
            getDocumentLink,
            previewDocuments,
            getFileName,
        } = dataStore;
        const label = getDocumentDisplayName(document, entityStore?.value);

        return (
            <DownloadControls
                label={label}
                selected={selected}
                actions={this.downloadControlActions}
                onClick={e => {
                    e.stopPropagation();
                    downloadDocuments([document], entityStore?.value);
                }}
                onPreviewClick={(e, elementOptions: PreviewActionOptions) => {
                    e.stopPropagation();
                    previewDocuments(
                        [document],
                        entityStore?.value,
                        elementOptions
                    );
                }}
                href={getDocumentLink([document], entityStore?.value)}
                onChange={() => this.handleOnChange(document)}
                showCount={false}
                count={1}
                theme={{
                    ...theme,
                    ...{
                        DownloadControls:
                            theme.DocumentDropdownDownloadControls,
                        DownloadButton: {
                            color: 'inherit',
                            hover: {
                                color: 'inherit',
                            },
                            ...theme.DocumentDropdownDownloadButton,
                        },
                        DownloadButtonIcon:
                            theme.DocumentDropdownDownloadButtonIcon,
                        PreviewButtonIcon:
                            theme.DocumentDropdownPreviewButtonIcon,
                    },
                }}
                showLabel={showLabel}
                fileName={getFileName([document], entityStore?.value)}
                showCheckbox={showCheckbox}
                metricContext={`documentMenu-listItem-${camelCase(
                    label
                )}-${metricContext}`}
                document={document}
                documentType={documentType}
                superscriptScope={superscriptScope}
                disableExtendedInfo={disableExtendedInfo}
            />
        );
    }

    render() {
        const { theme, document, displayOption = 'dropdown' } = this.props;
        return displayOption === 'dropdown' ? (
            <DropDownDocumentListItem
                key={document.clientCode}
                {...theme.DocumentListItem}
            >
                {this.downloadControls()}
            </DropDownDocumentListItem>
        ) : (
            <InlineDocumentListItem
                key={document.clientCode}
                {...theme.DocumentListItem}
            >
                {this.downloadControls()}
            </InlineDocumentListItem>
        );
    }
}

export default withTheme(DocumentMenuListItem) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DocumentMenuListItem.tsx